import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  List,
  ListItem,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import FileOpenOutlineIcon from "@mui/icons-material/FileOpenOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { apiRequest } from "@/helper/api";
import type { RootState } from "@/components/state/store";
import RoundedContentBox from "@/components/general/roundedContentBox";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericNotification from "@/components/notification/genericNotification";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import {
  useCompanySetupCompletion,
  type CompanySetupTarget,
} from "@/components/company/useCompanySetupCompletion";

interface DraftCompany extends CompanySetupTarget {
  can_exit_draft_mode?: boolean;
}

interface CompanyPage {
  count: number;
  results: DraftCompany[];
}

const PAGE_SIZE = 10;

export default function DraftCompaniesWidget() {
  const { t, i18n } = useTranslation();
  const paginationLocalization =
    i18n.resolvedLanguage === "en" ? MRT_Localization_EN : MRT_Localization_DE;
  const permissions = useSelector(
    (state: RootState) => state.login.permissions,
  );
  const [data, setData] = useState<CompanyPage>({ count: 0, results: [] });
  const [page, setPage] = useState(1);
  const [refreshSignal, setRefreshSignal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { handleError, errorModal } = useErrorModal();

  const refresh = () => setRefreshSignal((previous) => !previous);
  const {
    completeSetup,
    openAnchorData,
    pendingCompanyId,
    setupIncompleteModal,
  } = useCompanySetupCompletion({
    onSuccess: () => {
      setShowSuccess(true);
      setPage(1);
      refresh();
    },
    onError: handleError,
    onRejected: refresh,
  });

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadFailed(false);
    apiRequest<CompanyPage>({
      method: "get",
      entity: "company",
      parametersToRender: {
        draft_mode: true,
        subject: "true",
        include_setup_status: "true",
        ordering: "name",
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      },
      shouldNotImpersonate: true,
      disableGlobalSpinner: true,
      config: { signal: controller.signal },
    })
      .then(({ data: response }) => {
        if (controller.signal.aborted) return;
        setData(response);
        setPage((current) =>
          Math.min(current, Math.max(1, Math.ceil(response.count / PAGE_SIZE))),
        );
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [page, refreshSignal]);

  return (
    <>
      {(data.count > 0 || loadFailed) && (
        <Grid container item columnSpacing={"1rem"} rowSpacing={"1rem"}>
          <Grid item xs={12} sm={12} md={6}>
            <RoundedContentBox>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography component="h2" variant="h6">
                  {t("draft_companies_title")}
                </Typography>
                {!loadFailed && data.count > 0 && (
                  <Chip size="small" label={data.count} />
                )}
              </Box>
              {isLoading && (
                <LinearProgress aria-label={t("loading")} color="secondary" />
              )}
              {loadFailed ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={refresh}>
                      {t("retry")}
                    </Button>
                  }>
                  {t("draft_companies_load_error")}
                </Alert>
              ) : (
                <>
                  <List disablePadding aria-label={t("draft_companies_title")}>
                    {data.results.map((company, index) => (
                      <ListItem
                        key={company.id}
                        disableGutters
                        divider={index < data.results.length - 1}
                        sx={{ gap: 2, py: 1.25 }}>
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}>
                          {company.name}
                        </Typography>
                        <Box display="flex" gap={0} flexShrink={0}>
                          {permissions.includes("frontend_view_anchordata") && (
                            <Tooltip
                              title={t("draft_companies_open_anchor_data")}
                              arrow
                              placement="top"
                              enterDelay={500}
                              enterNextDelay={500}
                            >
                              <span>
                                <GenericButton
                                  variant="icon"
                                  color="iconBase"
                                  aria-label={`${t("draft_companies_open_anchor_data")}: ${company.name}`}
                                  disabled={
                                    !company.subject?.id && !company.subject_id
                                  }
                                  onClick={() => openAnchorData(company)}
                                  style={{ padding: "0.25rem" }}>
                                  <FileOpenOutlineIcon />
                                </GenericButton>
                              </span>
                            </Tooltip>
                          )}
                          {company.can_exit_draft_mode === true &&
                            permissions.includes("change_company") && (
                              <Tooltip
                                title={t("company_setup_complete_action")}
                                arrow
                                placement="top"
                                enterDelay={500}
                                enterNextDelay={500}
                              >
                                <span>
                                  <GenericButton
                                    variant="icon"
                                    color="secondary"
                                    aria-label={`${t("company_setup_complete_action")}: ${company.name}`}
                                    disabled={
                                      isLoading || pendingCompanyId !== null
                                    }
                                    onClick={() => completeSetup(company)}
                                    style={{ padding: "0.25rem" }}>
                                    <CheckCircleOutlineIcon />
                                  </GenericButton>
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                  {data.count > PAGE_SIZE && (
                    <TablePagination
                      component="div"
                      count={data.count}
                      page={page - 1}
                      rowsPerPage={PAGE_SIZE}
                      rowsPerPageOptions={[]}
                      onPageChange={(_event, next) => setPage(next + 1)}
                      disabled={isLoading}
                      showFirstButton
                      showLastButton
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${paginationLocalization.of} ${count}`
                      }
                      getItemAriaLabel={(type) =>
                        ({
                          first: paginationLocalization.goToFirstPage,
                          previous: paginationLocalization.goToPreviousPage,
                          next: paginationLocalization.goToNextPage,
                          last: paginationLocalization.goToLastPage,
                        })[type]
                      }
                      sx={{
                        border: 0,
                        mt: 2,
                        "& .MuiTablePagination-toolbar": { minHeight: 0, p: 0 },
                        "& .MuiTablePagination-displayedRows": { m: 0 },
                        "& .MuiTablePagination-actions": { ml: 1 },
                        "& .MuiIconButton-root": { p: 0.5 },
                      }}
                    />
                  )}
                </>
              )}
            </RoundedContentBox>
          </Grid>
        </Grid>
      )}
      {setupIncompleteModal}
      {errorModal}
      <GenericNotification
        message={showSuccess ? t("company_setup_success") : null}
        variant="success"
        handleCloseSnackbar={() => setShowSuccess(false)}
      />
    </>
  );
}
